/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { useMutation, useQuery } from "@apollo/client";
import React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { bindTrigger, bindMenu } from "material-ui-popup-state";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Button } from "components/haven-design";

import {
  ChangeInterestStatus,
  ChangeInterestStatusVariables,
  RelatedInterestActions,
  RelatedInterestActionsVariables,
  skipInterestAction,
  skipInterestActionVariables,
  QueryInterestProfile_interest_interestList_property as PropertyType,
  syncOneSiteGuestCard,
  syncOneSiteGuestCardVariables,
  Feature,
  FormType,
  sendForm,
  sendFormVariables,
} from "types/gql-types";

import { apolloClient as client } from "data/graphql";
import logger from "services/logger";
import { toLocalIsoDate } from "utils/date";
import { InterestStatus, statusFromStage } from "model/interest/status";
import { useFeature, usePropertyFeature } from "components/feature";
import { SelectAutocomplete } from "components/haven-design/form/select";
import { useSnackbar } from "components/haven-design/snackbar";
import ReasonModal, { Action } from "pages/dashboard/interests-profile/components/reason-modal";
import {
  CHANGE_STATUS,
  QUERY,
  SKIP_INTEREST,
  SEND_FULL_APP,
  SYNC_ONE_SITE_GUEST_CARD,
} from "pages/dashboard/interests-profile/components/actions-query";
import SendFullAppModal from "pages/dashboard/interests/send-full-app-modal";


import ActionItem from "./action-item";
import { MoveInModal } from "./modals/move-in";
import RequestFormsModal from "./modals/request-forms";
import ResetPasswordFormContainer from "./modals/reset-password-form-container";
import PrintableDownload from "./printable-download";


type Props = {
  property: PropertyType;
  currentInterestName: string;
  currentInterestID: string;
  interestListID: string;
  status: string;
  setBatchActionsModalFocusedAction: React.Dispatch<React.SetStateAction<Action | undefined>>;
  sendFullAppToInterestsEnabled: boolean;
}

const ACTIONS: Action[] = [
  Action.AddDocument,
  Action.AddNote,
  Action.LogCall,
  Action.MovedIn,
  Action.OnWaitlist,
  Action.Removed,
  Action.ResetPassword,
  Action.RequestForms,
  Action.SendFullApp,
  Action.SendMessage,
  Action.Skip,
];

const ActionButton: React.FC<Props> = (props: Props) => {
  const {
    property,
    currentInterestID,
    interestListID,
    currentInterestName,
    status: currentStatus,
    setBatchActionsModalFocusedAction,
    sendFullAppToInterestsEnabled,
  } = props;
  const { openSnackbar } = useSnackbar();

  const statusOptions = property.stages.filter((stage) => stage.enabled);

  // Local state for the confirmation modal displayed when attempting to
  // change the interest status.
  const [confirming, setConfirming] = React.useState<Action | null>(null);
  const [reason, setReason] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [isSendFullAppOpen, setIsSendFullAppOpen] = React.useState(false);

  const [isRequestFormsOpen, setIsRequestFormsOpen] = React.useState(false);
  const [isResetPasswordFormOpen, setIsResetPasswordFormOpen] = React.useState(false);

  const popupState = usePopupState({ variant: "popover", popupId: "slideout-actions-menu" });

  // GraphQL queries and mutations.
  const variables = { interestID: currentInterestID, interestListID };

  const [skipInterest] = useMutation<
  skipInterestAction,
  skipInterestActionVariables
  >(SKIP_INTEREST);

  const query = useQuery<RelatedInterestActions, RelatedInterestActionsVariables>(QUERY, {
    variables,
  });

  const [setStatus] = useMutation<
  ChangeInterestStatus,
  ChangeInterestStatusVariables
  >(CHANGE_STATUS, { refetchQueries: ["activityFeed", "QueryInterestProfile", "ApplicantSearch", "RelatedInterestActions", "getActivityFeed"] });

  const [sendFullApp] = useMutation<sendForm, sendFormVariables>(SEND_FULL_APP, { client });


  const [syncOneSite] = useMutation<
  syncOneSiteGuestCard,
  syncOneSiteGuestCardVariables>(SYNC_ONE_SITE_GUEST_CARD);

  const onesiteSyncEnabled = useFeature(Feature.ONESITE_SYNC);
  const hasRequestForms = usePropertyFeature(property.id, Feature.REQUEST_FORMS);
  const hasDocumentUpload = usePropertyFeature(property.id, Feature.DOCUMENT_UPLOAD);

  if (query.error || query.loading || !query?.data?.interest) {
    return null;
  }

  const { interest } = query.data;

  const onResetConfirmation = () => {
    setConfirming(null);
    setReason("");
  };

  const isActionVisible = (action: Action, stage: string): boolean => {
    const status = statusFromStage(stage);
    switch (action) {
      case Action.SendFullApp:
        return sendFullAppToInterestsEnabled;
      case Action.RequestForms:
        return hasRequestForms ?? false;
      case Action.MovedIn:
        return status === InterestStatus.Active;
      case Action.Removed:
        return status === InterestStatus.Active;
      case Action.ResetPassword:
        return Boolean(interest.userId);
      case Action.Skip:
        return status === InterestStatus.Active;
      case Action.OnWaitlist:
        return status === InterestStatus.Removed || status === InterestStatus.MovedIn;
      case Action.AddDocument:
        return hasDocumentUpload ?? false;
      default:
        return true;
    }
  };

  const onSubmitNewActivity = () => {
    if (confirming === null) {
      return;
    }
    skipInterest({
      variables: {
        data: {
          resourceId: interest.id,
          note: reason || null,
        },
      },
      refetchQueries: ["QueryInterestProfile"],
    })
      .then(() => {
        openSnackbar({ message: "Success", status: "success" });
        onResetConfirmation();
      })
      .then(() => setIsSubmitting(false))
      .catch((e) => {
        logger.sendError(e);
        onResetConfirmation();
        openSnackbar({ message: "There was a problem skipping this applicant.", status: "error" });
        setIsSubmitting(false);
      });
  };

  const onStatusChange = () => {
    if (confirming === null) {
      return;
    }

    const statusVariables = {
      interestId: currentInterestID,
      status: confirming,
      reason,
    };

    const request = setStatus({ variables: statusVariables });
    request
      .then(() => query.refetch())
      .then(() => openSnackbar({ message: "Updated application status.", status: "success" }))
      .then(onResetConfirmation)
      .then(() => setIsSubmitting(false));

    request.catch((e) => {
      logger.sendError(e);
      onResetConfirmation();
      openSnackbar({ message: "There was a problem updating the application status.", status: "error" });
      setIsSubmitting(false);
    });
  };

  const onModalSubmit = () => {
    setIsSubmitting(true);

    if (confirming === "Skip") {
      onSubmitNewActivity();
    } else {
      onStatusChange();
    }
  };

  const closeSendFullApp = () => setIsSendFullAppOpen(false);
  const onSendFullApp = (deadline: Date) => {
    const fullAppVariables = {
      interestId: currentInterestID,
      type: FormType.FULL_APPLICATION,
      deadline: toLocalIsoDate(deadline),
    };

    sendFullApp({ variables: fullAppVariables })
      .then(() => {
        openSnackbar({ message: "Full application request sent.", status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "There was a problem sending full application request.", status: "error" });
      })
      .finally(closeSendFullApp);
  };

  const onRequestForms = ():void => {
    setIsRequestFormsOpen(false);
    openSnackbar({ message: "Success", status: "success" });
  };

  const onSyncOneSite = (interestId: string) => {
    syncOneSite({ variables: { interestId } })
      .then(() => {
        openSnackbar({ message: "OneSite synced.", status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Failed to sync to OneSite.", status: "error" });
      });
  };

  const setActions = {
    setIsSendFullAppOpen,
    setConfirming,
    setBatchActionsModalFocusedAction,
    setIsRequestFormsOpen,
    setIsResetPasswordFormOpen,
  };

  return (
    <>
      <Stack
        spacing={{ sm: 1, md: 2 }}
        direction="row"
        justifyContent={{ xs: "flex-start", md: "flex-end" }}
        alignItems="center"
        sx={{ flexGrow: 1, px: { xs: 1, lg: 0 }, pb: { xs: 1, lg: 0 } }}
      >
        <SelectAutocomplete
          disableClearable
          fullWidth
          label="Status"
          margin="dense"
          size="small"
          onChange={(_, value) => setConfirming(value as Action)}
          options={statusOptions.map(option => option.name)}
          value={currentStatus}
          data-cy="changeStatusSlideout"
          sx={{ flexGrow: 1, minWidth: { sm: 300, md: 200 } }}
        />

        <Box sx={{ pr: 2 }}>
          <Button
            id="slideout-actions"
            size="S"
            color="primary"
            {...bindTrigger(popupState)}
          >
          Actions&nbsp;<span className="caret" style={{ marginLeft: 4 }} />
          </Button>
          <Menu {...bindMenu(popupState)}>
            {ACTIONS.filter((action) => isActionVisible(action, interest.status?.name ?? ""))
              .map((action) => (
                <ActionItem
                  key={action}
                  action={action}
                  setActions={setActions}
                  closeModal={popupState.close}
                />
              ))}

            <PrintableDownload interestID={currentInterestID} onClick={popupState.close} />

            {onesiteSyncEnabled &&
                  <MenuItem onClick={() => {
                    onSyncOneSite(interest.id);
                    popupState.close();
                  }}>
                    Sync to OneSite
                  </MenuItem>
            }
          </Menu>
        </Box>

        <SendFullAppModal
          interestName={currentInterestName}
          onSubmit={(deadline: Date) => onSendFullApp(deadline)}
          onRequestClose={closeSendFullApp}
          onCancel={closeSendFullApp}
          isOpen={isSendFullAppOpen}
        />

        <RequestFormsModal
          propertyId={property.id}
          interestId={currentInterestID}
          isOpen={isRequestFormsOpen}
          onSubmit={() => onRequestForms()}
          onRequestClose={() => setIsRequestFormsOpen(false)}
          onCancel={() => setIsRequestFormsOpen(false)}
        />

        <ResetPasswordFormContainer
          userId={interest.userId}
          currentInterestName={currentInterestName}
          isOpen={isResetPasswordFormOpen}
          onRequestClose={() => setIsResetPasswordFormOpen(false)}
          onCancel={() => setIsResetPasswordFormOpen(false)}
        />

        <ReasonModal
          isOpen={confirming !== null && confirming !== Action.MovedIn}
          newStatus={confirming}
          currentStatus={currentStatus as Action}
          onSubmit={() => onModalSubmit()}
          onChange={(newReason) => setReason(newReason)}
          reason={reason}
          onRequestClose={() => onResetConfirmation()}
          onCancel={() => onResetConfirmation()}
          isSubmitting={isSubmitting}
        />

        <MoveInModal
          isOpen={confirming === Action.MovedIn}
          interestIds={[currentInterestID]}
          interestListId={interestListID}
          onRequestClose={() => onResetConfirmation()}
        />
      </Stack>
    </>
  );
};

export default ActionButton;
